import { useQuery } from "@tanstack/react-query";
import { fetchDashboardAdvanced } from "../../api/queries";
import { useLanguage } from "../../contexts/LanguageContext";
import BarList from "./components/BarList";
import CohortTable from "./components/CohortTable";
import ExportButton from "./components/ExportButton";
import LockedPreview from "./components/LockedPreview";
import MessageImpact from "./components/MessageImpact";
import WeeklyReportSwitch from "./components/WeeklyReportSwitch";
import { Panel, SectionError } from "./components/ui";
import { formatMonth, formatPercent } from "./format";
import { sampleAdvanced } from "./sampleData";

// The Advanced plan tab. Locked: sample data under the lock card, and the
// advanced endpoint is never requested.
export default function Advanced({ storeId, locked, teaser, weeklyReportEnabled, canManageStore }) {
  const { t, language } = useLanguage();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["dashboard", "advanced", storeId],
    queryFn: () => fetchDashboardAdvanced(storeId),
    enabled: !locked && !!storeId,
  });

  if (locked) {
    const cohort = teaser?.advanced;
    const hasNumber = cohort && Number.isFinite(cohort.month_1_retention) && cohort.cohort_month;
    return (
      <LockedPreview
        title={t("dashboard.locked.advanced.title")}
        body={
          hasNumber
            ? t("dashboard.locked.advanced.body", {
                value: formatPercent(cohort.month_1_retention, language),
                month: formatMonth(cohort.cohort_month, language),
              })
            : t("dashboard.locked.advanced.generic")
        }
        upgradeLabel={t("dashboard.locked.advanced.upgrade")}
      >
        <AdvancedView data={sampleAdvanced} preview />
      </LockedPreview>
    );
  }

  if (isError) return <SectionError onRetry={() => refetch()} />;

  return (
    <AdvancedView
      storeId={storeId}
      data={data}
      loading={isLoading || !data}
      weeklyReportEnabled={weeklyReportEnabled}
      canManageStore={canManageStore}
    />
  );
}

export function AdvancedView({
  storeId,
  data,
  loading = false,
  preview = false,
  weeklyReportEnabled = true,
  canManageStore = true,
}) {
  const { t, language } = useLanguage();
  const channels = data?.points_by_channel || [];

  const channelName = (channel) => {
    const key = `dashboard.advanced.channels.${channel}`;
    const label = t(key);
    return label === key ? t("dashboard.advanced.channels.other") : label;
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-2xl text-[15px] leading-relaxed text-gray-700">
          {t("dashboard.advanced.summary")}
        </p>
        <div className="flex flex-wrap items-center gap-2.5">
          <WeeklyReportSwitch
            storeId={preview ? null : storeId}
            enabled={weeklyReportEnabled}
            disabled={!canManageStore}
          />
          {preview ? (
            <button
              type="button"
              className="inline-flex min-h-11 items-center rounded-[10px] bg-gray-900 px-4 text-sm font-semibold text-white"
            >
              {t("dashboard.export.button")}
            </button>
          ) : (
            canManageStore && <ExportButton storeId={storeId} type="cohorts" />
          )}
        </div>
      </div>

      <CohortTable cohorts={data?.cohorts} loading={loading} />

      <div className="flex flex-wrap items-stretch gap-4">
        <MessageImpact impact={data?.message_impact} loading={loading} />
        <Panel
          title={t("dashboard.advanced.channels.title")}
          subtitle={t("dashboard.advanced.channels.subtitle")}
          className="flex-[1_1_300px]"
        >
          <BarList
            loading={loading}
            label={t("dashboard.advanced.channels.title")}
            items={channels.map((c) => ({
              key: c.channel,
              name: channelName(c.channel),
              value: c.share ?? 0,
              display: formatPercent(c.share, language),
            }))}
          />
        </Panel>
      </div>
    </div>
  );
}
